import { useEffect, useState } from 'react';
import { formatBodyFat } from '../lib/metric-format';
import { Eyebrow } from '../concept/eyebrow';
import { ActionButton } from '../concept/action-button';

/** Body-fat bounds the slider allows. Outside this is not a goal, it is a typo. */
const MIN = 5;
const MAX = 45;

const clamp = (n: number) => Math.min(MAX, Math.max(MIN, n));
const at = (n: number) => ((clamp(n) - MIN) / (MAX - MIN)) * 100;

export interface Zone {
  label: string;
  min: number;
  max: number;
}

const zoneAt = (zones: Zone[], value: number) =>
  zones.find((z) => value >= z.min && value < z.max) ?? zones[zones.length - 1];

/**
 * Target setting, with the zones shown and the traffic light still gone.
 *
 * Those are not in conflict. What the accessibility work ruled out was the
 * four-hue strip: no four-band palette passes every check, because
 * green-to-amber adjacency IS the protan confusion axis, and its conclusion
 * was that the band *labels* were carrying the meaning. So the bands are drawn
 * as named regions with their boundaries marked, and which one a reading falls
 * in is said in words — "Overfat at 41" — rather than left to a colour a
 * reader may not be able to separate.
 *
 * The target commits on a button, not on release. Dragging past a value is
 * how a slider is used; writing to the client's record every time the finger
 * lifts made an exploratory drag into four saved targets.
 */
export const TargetSlider: React.FC<{
  currentPercent: number;
  /** The committed target, or undefined when none is set. */
  targetPercent?: number;
  /** Bands for this client's age and gender. */
  zones: Zone[];
  age?: number;
  onPreview?: (value: number) => void;
  /**
   * Saves the value. Resolving false means the save failed: the slider goes
   * back to the saved target rather than sit on a value nobody kept.
   */
  onCommit?: (value: number) => void | Promise<boolean>;
  saving?: boolean;
  /**
   * False when the client has never been weighed: the body is simulated from
   * the target alone, with no lean mass to hold. Defaults to true.
   */
  holdsLean?: boolean;
}> = ({
  currentPercent,
  targetPercent,
  zones,
  age,
  onPreview,
  onCommit,
  saving,
  holdsLean = true,
}) => {
  // A whole-number starting point: the slider steps in whole points, while
  // Now is shown at one decimal like every other body-fat reading.
  const committed = targetPercent ?? Math.max(MIN, Math.round(currentPercent) - 5);
  const [draft, setDraft] = useState(committed);
  // `saving` is true whenever the projected body is loading — including on
  // first open, when nobody has pressed anything. "Setting…" then describes a
  // save that is not happening, so it is said only after a press here.
  const [pressed, setPressed] = useState(false);
  useEffect(() => {
    if (!saving) setPressed(false);
  }, [saving]);

  // Follow the committed value when it changes underneath — a save landing, or
  // another surface setting it — but never while the coach is mid-drag.
  useEffect(() => setDraft(committed), [committed]);

  const dirty = draft !== targetPercent;
  const nowZone = zoneAt(zones, currentPercent);
  const targetZone = zoneAt(zones, draft);

  return (
    <div
      data-testid="target-slider"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <Eyebrow>Body fat target</Eyebrow>
        <div className="tabular flex items-baseline gap-4 text-xs">
          <span className="text-foreground-soft">
            Now <span data-testid="target-now" className="font-semibold text-foreground">{formatBodyFat(currentPercent)}</span>
            {nowZone && <span className="ml-1 text-muted-foreground">· {nowZone.label}</span>}
          </span>
          <span className="text-foreground-soft">
            Target{' '}
            <span className="font-semibold text-[hsl(var(--projected))]">{draft} %</span>
            {targetZone && (
              <span className="ml-1 text-muted-foreground">· {targetZone.label}</span>
            )}
          </span>
        </div>
      </div>

      <div className="relative mt-1 h-[52px]">
        {/* Zones, as named regions rather than a colour ramp. Alternating
            ground separates them without implying an ordering of good to bad. */}
        <div className="absolute inset-x-0 top-0 h-5" aria-hidden="true">
          {zones.map((z, i) => {
            const left = at(z.min);
            const width = at(Math.min(z.max, MAX)) - left;
            if (width <= 0) return null;
            return (
              <div
                key={z.label}
                className={`absolute top-0 flex h-5 items-center justify-center overflow-hidden border-l border-divider first:border-l-0 ${
                  i % 2 ? 'bg-shell' : 'bg-secondary'
                }`}
                style={{ left: `${left}%`, width: `${width}%` }}
              >
                <span className="truncate px-1 font-mono text-[8px] uppercase tracking-[0.06em] text-muted-foreground">
                  {z.label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="absolute inset-x-0 top-7 h-[3px] rounded-sm bg-divider" />
        {/* The gap between here and the goal, dashed because it has not happened */}
        <div
          className="absolute top-7 h-[3px] rounded-sm"
          style={{
            left: `${Math.min(at(currentPercent), at(draft))}%`,
            width: `${Math.abs(at(draft) - at(currentPercent))}%`,
            background:
              'repeating-linear-gradient(90deg, hsl(var(--projected-line)) 0 6px, transparent 6px 11px)',
          }}
        />
        <div
          className="absolute top-[23px] h-[11px] w-[11px] -ml-[5px] rounded-full bg-foreground"
          style={{ left: `${at(currentPercent)}%` }}
          title={`Now: ${formatBodyFat(currentPercent)}`}
        />
        <div
          className="absolute top-[22px] h-[15px] w-[15px] -ml-[7px] rounded-full border-[3px] border-[hsl(var(--projected))] bg-card"
          style={{ left: `${at(draft)}%` }}
        />

        <input
          type="range"
          min={MIN}
          max={MAX}
          step={1}
          value={draft}
          aria-label="Body fat target"
          aria-valuetext={`${draft} per cent${targetZone ? `, ${targetZone.label}` : ''}`}
          onChange={(e) => {
            const v = Number(e.target.value);
            setDraft(v);
            onPreview?.(v);
          }}
          className="absolute inset-x-0 top-5 h-6 w-full cursor-pointer appearance-none bg-transparent
                     [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-transparent
                     [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6
                     [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-transparent"
        />

        <div className="tabular absolute left-0 top-[42px] font-mono text-[8.5px] font-medium tracking-[0.08em] text-muted-foreground">
          {MIN} %
        </div>
        <div className="tabular absolute right-0 top-[42px] font-mono text-[8.5px] font-medium tracking-[0.08em] text-muted-foreground">
          {MAX} %
        </div>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-2">
        <ActionButton
          variant="primary"
          onClick={async () => {
            setPressed(true);
            const saved = await onCommit?.(draft);
            if (saved === false) {
              setPressed(false);
              setDraft(committed);
              onPreview?.(committed);
            }
          }}
          disabled={!dirty || saving}
          testId="set-target"
        >
          {pressed && saving
            ? 'Setting…'
            : targetPercent === undefined
              ? 'Set target'
              : 'Update target'}
        </ActionButton>
        {dirty && !saving && (
          <button
            type="button"
            onClick={() => {
              setDraft(committed);
              // Tell the page too, or the figures and the twin keep showing
              // the value that was just cancelled.
              onPreview?.(committed);
            }}
            className="text-[11px] font-medium text-muted-foreground hover:underline"
          >
            Cancel
          </button>
        )}
        <p className="ml-auto text-[10.5px] text-muted-foreground">
          {typeof age === 'number' && zones.length ? `Zones for age ${age}. ` : ''}
          {holdsLean
            ? 'The projection holds lean mass. No date is forecast.'
            : 'The body is simulated from the target. No date is forecast.'}
        </p>
      </div>
    </div>
  );
};
